import { usePageMeta } from "@/lib/seo";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useCurrency } from "@/context/CurrencyContext";
import { toast } from "sonner";
import { Plus, Sparkles } from "lucide-react";
import { typeLabel } from "@/lib/labels";

const TYPES = [
  { key: null, label: "All" },
  { key: "ebook", label: "E-books" },
  { key: "audio", label: "Meditations" },
  { key: "physical", label: "Physical" },
  { key: "bundle", label: "Collections" },
];

export default function Shop() {
  usePageMeta(
    "Shop — E-books, Journals & Affirmation Decks | Just be you",
    "Original self-help e-books, guided meditations, hand-bound journals and affirmation decks — all created by Mehjabin. Dual pricing in ₹ and $."
  );
  const [products, setProducts] = useState([]);
  const [filter, setFilter] = useState(null);
  const { addItem } = useCart();
  const { format } = useCurrency();

  useEffect(() => {
    const params = filter ? { type: filter } : {};
    api.get("/products", { params }).then((r) => setProducts(r.data));
  }, [filter]);

  const handleAdd = (p) => {
    addItem(p, 1);
    toast.success(`Added "${p.title}" to your basket`);
  };

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24 relative" data-testid="shop-page">
      <div className="absolute -top-20 right-0 w-96 h-96 bg-peach/40 organic-blob blur-3xl -z-0 pointer-events-none" />

      <div className="relative grid grid-cols-1 md:grid-cols-12 gap-8 mb-16 items-end">
        <div className="md:col-span-7">
          <div className="overline mb-5">The Shop</div>
          <h1 className="font-serif text-5xl md:text-6xl text-lapis font-light leading-tight">
            Soft tools to<br />
            <em className="text-peach-deep">come home</em> to yourself.
          </h1>
        </div>
        <div className="md:col-span-5 text-ink/65 leading-relaxed">
          Each offering — digital and physical — was made slowly, with intention.
          Choose what calls.
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-12 relative" data-testid="shop-filters">
        {TYPES.map((t) => (
          <button
            key={t.label}
            onClick={() => setFilter(t.key)}
            data-testid={`shop-filter-${t.key || "all"}`}
            className={`px-5 py-2 rounded-full text-xs uppercase tracking-[0.15em] border transition font-semibold ${
              filter === t.key
                ? "bg-lapis text-canvas border-lapis"
                : "border-lapis/25 text-ink/70 hover:text-lapis hover:border-lapis bg-white/60"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10 relative">
        {products.map((p, i) => (
          <motion.div
            key={p.id}
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: (i % 6) * 0.06 }}
            className="group relative"
          >
            <Link to={`/shop/${p.slug}`} className="block" data-testid={`product-card-${p.slug}`}>
              <div className="aspect-[4/5] overflow-hidden rounded-[2rem] relative mb-5">
                <img src={p.image} alt={p.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1500ms] group-hover:scale-105" />
                <div className="absolute top-4 left-4 overline bg-canvas/85 backdrop-blur px-3 py-1 rounded-full">{typeLabel(p.type)}</div>
                {p.preorder && (
                  <div className="absolute bottom-4 left-4 bg-peach text-white text-[10px] uppercase tracking-[0.18em] px-3 py-1.5 rounded-full font-semibold" data-testid={`product-preorder-badge-${p.slug}`}>
                    Pre-order · {p.preorder_note?.replace("Delivered ", "") || "coming soon"}
                  </div>
                )}
                {p.exclusive && (
                  <div className="absolute top-4 right-14 flex items-center gap-1.5 bg-lapis text-canvas text-[10px] uppercase tracking-[0.18em] px-3 py-1 rounded-full font-semibold">
                    <Sparkles size={10} /> Only here
                  </div>
                )}
              </div>
              <h3 className="font-serif text-2xl text-ink leading-tight group-hover:text-lapis transition-colors">{p.title}</h3>
              <p className="mt-2 text-sm text-ink/60 line-clamp-2">{p.description}</p>
              <div className="mt-4 flex items-center justify-between">
                <span className="font-serif text-xl text-lapis">{format(p.price, p.price_inr)}</span>
                <span className="text-xs tracking-[0.18em] uppercase text-ink/50 group-hover:text-lapis transition">View →</span>
              </div>
            </Link>
            <button
              onClick={(e) => { e.preventDefault(); handleAdd(p); }}
              className="absolute top-4 right-4 glass-strong w-10 h-10 rounded-full flex items-center justify-center text-lapis hover:bg-peach hover:text-white transition-all duration-300"
              data-testid={`product-add-cart-${p.slug}`}
              aria-label="Add to cart"
            >
              <Plus size={16} strokeWidth={2} />
            </button>
          </motion.div>
        ))}
      </div>

      {products.length === 0 && (
        <div className="text-center text-ink/50 font-serif italic text-2xl py-32" data-testid="shop-empty">
          Nothing here yet — check back soon.
        </div>
      )}
    </div>
  );
}
